// src/helpers/ablyPresence.js
import { useEffect, useState } from "react";
import { Realtime } from "ably";
import ABLY_KEY from "../ablyConfig";

let ablyInstance = null;
const presenceChannelName = "online-users";

const presenceCounts = {};
const listeners = new Set();

function notifyListeners() {
  listeners.forEach((cb) => cb({ ...presenceCounts }));
}

function updatePresenceCounts(userId, action) {
  const id = userId.toString();
  if (action === "enter") {
    presenceCounts[id] = (presenceCounts[id] || 0) + 1;
    console.log(`User ${id} entered, now ${presenceCounts[id]} connection(s)`);
  } else if (action === "leave") {
    if (presenceCounts[id]) {
      presenceCounts[id] -= 1;
      if (presenceCounts[id] <= 0) {
        delete presenceCounts[id];
        console.log(`User ${id} left, no connections remaining`);
      } else {
        console.log(`User ${id} left one tab, now ${presenceCounts[id]} connection(s)`);
      }
    }
  }
  notifyListeners();
}

// Build presenceCounts from the current presence.members map
function refreshFromMembers(channel) {
  console.log("Refreshing from members map");
  const members = channel.presence.members;
  console.log("members object:", members);
  
  // Clear existing counts
  Object.keys(presenceCounts).forEach((key) => delete presenceCounts[key]);
  
  // Collect member objects into an array regardless of structure
  let membersArray = [];
  if (!members) {
    console.warn("No members object");
  } else if (Array.isArray(members)) {
    membersArray = members;
  } else if (typeof members.forEach === 'function') {
    // It's a Map or Set-like (has forEach)
    members.forEach(m => membersArray.push(m));
  } else if (typeof members.values === 'function') {
    // It's an iterable (like Map.values)
    membersArray = Array.from(members.values());
  } else if (typeof members === 'object') {
    // Fallback: treat as plain object (keys are connectionIds, values are members)
    membersArray = Object.values(members);
  }
  
  // Now iterate and update counts
  membersArray.forEach((member) => {
    if (member && member.data && member.data.user_id) {
      const id = member.data.user_id.toString();
      presenceCounts[id] = (presenceCounts[id] || 0) + 1;
    }
  });
  
  console.log("Current presence counts:", presenceCounts);
  notifyListeners();
}

export const initPresenceForLoggedInUser = async (userId, userName) => {
  if (!userId) {
    console.error("initPresenceForLoggedInUser called without userId");
    return;
  }

  console.log("initPresenceForLoggedInUser called", userId, userName);

  // If we already have an instance, just update presence and refresh
  if (ablyInstance) {
    console.log("Ably instance already exists, updating presence");
    const channel = ablyInstance.channels.get(presenceChannelName);
    await channel.presence.update({ user_id: userId, name: userName });
    // Refresh from members map
    refreshFromMembers(channel);
    return;
  }

  const clientId = `${userId}-${Date.now()}`;
  console.log("Creating new Ably instance with clientId", clientId);
  ablyInstance = new Realtime({ key: ABLY_KEY, clientId, reconnect: true });

  console.log("Waiting for connection...");
  await new Promise((resolve) => {
    if (ablyInstance.connection.state === "connected") {
      console.log("Already connected");
      resolve();
    } else {
      ablyInstance.connection.once("connected", () => {
        console.log("Connection established");
        resolve();
      });
    }
  });

  const presenceChannel = ablyInstance.channels.get(presenceChannelName);
  console.log("Got presence channel");

  console.log("Entering presence...");
  try {
    await presenceChannel.presence.enter({ user_id: userId, name: userName });
    console.log("Entered presence");
  } catch (enterError) {
    console.error("Failed to enter presence:", enterError);
    return;
  }

  console.log("Subscribing to presence events");
  presenceChannel.presence.subscribe((presenceData) => {
    console.log("Presence event received", presenceData);
    const { action, data } = presenceData;
    if (!data || !data.user_id) return;
    updatePresenceCounts(data.user_id, action);
  });

  // Wait for initial presence sync (members map to be populated)
  console.log("Waiting for presence sync...");
  await new Promise((resolve) => {
    // If members already have data, resolve immediately
    if (presenceChannel.presence.members.size > 0) {
      console.log("Members already present");
      resolve();
      return;
    }
    // Otherwise wait for 'sync' event
    const onSync = () => {
      console.log("Presence sync complete");
      presenceChannel.presence.off("sync", onSync);
      resolve();
    };
    presenceChannel.presence.on("sync", onSync);
    // Fallback timeout in case sync never fires
    setTimeout(() => {
      presenceChannel.presence.off("sync", onSync);
      console.warn("Presence sync timeout – using current members");
      resolve();
    }, 3000);
  });

  // Now refresh from the members map
  refreshFromMembers(presenceChannel);

  window.addEventListener("beforeunload", () => {
    if (presenceChannel.state === "attached") {
      presenceChannel.presence.leave();
    }
  });
};

export const useOnlineStatus = (userId) => {
  const [isOnline, setIsOnline] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const handleChange = (counts) => {
      setIsOnline(!!counts[userId]);
    };
    listeners.add(handleChange);
    handleChange(presenceCounts);
    return () => listeners.delete(handleChange);
  }, [userId]);

  return isOnline;
};

export const logoutPresence = async () => {
  if (!ablyInstance) return;
  const channel = ablyInstance.channels.get(presenceChannelName);
  if (channel.state === "attached") {
    try {
      await channel.presence.leave();
      channel.detach();
    } catch (err) {
      console.error("Error leaving presence:", err);
    }
  }
  ablyInstance.close();
  ablyInstance = null;
  Object.keys(presenceCounts).forEach((key) => delete presenceCounts[key]);
  notifyListeners();
  console.log("Logged out from presence");
};